// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
/* Store Custom Code
import DataViewLoadingOverlay, {
    DataViewLoadingOverlayProps,
} from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
*/
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { baseGridProps } from "@store/Themes/common/props/baseGrid";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
// import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper"; // Store Custom Code
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import DataViewLoadingOverlay, {
    DataViewLoadingOverlayProps,
} from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { gridGapStyles } from "@toolkit/Themes/common/mixins/grid";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";

import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";

const topOrBottomContentZoneName = "TopOrBottomContent";
/* Store Custom Code - End */

export interface OrderLayoutProps extends ExpandStyles<OrderLayoutStyles>, DataViewLoadingOverlayProps {
    id: string;
    stickySideColumn?: boolean;
    // Store Custom Code
    displayLoadingText?: boolean;
    loadingText?: string;
    isPlacingOrder?: boolean;
    displayAdditionalMessage?: boolean;
    additionalMessageText?: string;
    isV2?: boolean;
    showTopZoneAtBottom?: boolean;
}

export interface OrderLayoutStyles {
    loadingOverlay?: LoadingOverlayProps;
    container?: GridContainerProps;
    mainColumn?: GridItemProps;
    sideColumnMobile?: GridItemProps;
    sideColumnDesktop?: GridItemProps;
    sideColumnWrapper?: InjectableCss;
    // Store Custom Code
    topOrBottomContentWrapper?: InjectableCss;
}

export const orderLayoutStyles: OrderLayoutStyles = {
    loadingOverlay: { minHeight: 150 },
    /* Store Custom Code - Start */
    container: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([40]), pxToRemArray([32]))}
        `,
    },
    /* Store Custom Code - End */
    mainColumn: {
        width: [12, 12, 12 /* Store Custom Code */, 8, 9],
        printWidth: 12,
        /* Store Custom Code - Start */
        css: css`
            flex-direction: column;
        `,
        /* Store Custom Code - End */
    },
    sideColumnMobile: {
        width: [12, 12, 12 /* Store Custom Code */, 0, 0],
        printWidth: 0,
    },
    sideColumnDesktop: {
        width: [0, 0, 0 /* Store Custom Code */, 4, 3],
        printWidth: 12,
    },
    sideColumnWrapper: {
        css: css`
            width: 100%;
        `,
    },
    /* Store Custom Code - Start */
    topOrBottomContentWrapper: {
        css: css`
            ${spacingStyles(pxToRem(32), pxToRem(32))}
            width: 100%;
        `,
    },
    /* Store Custom Code - End */
};

/* Store Custom Code - Start */
export const orderLayoutStylesV2 = mergeProps<OrderLayoutStyles>(orderLayoutStyles, {
    mainColumn: {
        width: [12, 12, 12, 8, 8],
        printWidth: 12,
    },
    sideColumnMobile: {
        width: [12, 12, 12, 0, 0],
        printWidth: 0,
    },
    sideColumnDesktop: {
        width: [0, 0, 0, 4, 4],
        printWidth: 12,
    },
});
/* Store Custom Code - End */

const OrderLayout: FC<OrderLayoutProps> = ({
    id,
    loading,
    loadComplete,
    dataView,
    preload,
    extendedStyles,
    overridedStyles,
    stickySideColumn = true,
    // Store Custom Code
    loadingText,
    displayLoadingText,
    isPlacingOrder,
    displayAdditionalMessage,
    additionalMessageText,
    isV2,
    showTopZoneAtBottom,
}) => {
    const styles = useExpandProps(isV2 ? orderLayoutStylesV2 : orderLayoutStyles, extendedStyles, overridedStyles); // Store Custom Code
    const isTopOrBottomZoneActive = useZoneIsActive(id, topOrBottomContentZoneName); // Store Custom Code
    const enableTopOrBottomZone = typeof showTopZoneAtBottom !== "undefined" && isTopOrBottomZoneActive; // Store Custom Code

    const sideColumnWrapperStyles = useMemo(() => {
        if (!stickySideColumn) {
            return styles.sideColumnWrapper;
        }

        return mergeProps(styles.sideColumnWrapper, {
            css: css`
                position: sticky;
                top: ${pxToRem(140 + 20) /* Store Custom Code - Add an extra 75 pixels to handle the sticky */};
            `,
        });
    }, [stickySideColumn, styles.sideColumnWrapper]);

    return (
        <DataViewLoadingOverlay
            dataView={dataView}
            loading={loading}
            loadComplete={loadComplete}
            preload={preload}
            {...styles.loadingOverlay}
            // Store Custom Code
            loadingText={loadingText}
            displayLoadingText={displayLoadingText}
            isPlacingOrder={isPlacingOrder}
            displayAdditionalMessage={displayAdditionalMessage}
            additionalMessageText={additionalMessageText}
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.sideColumnMobile}>
                    <Zone contentId={id} zoneName="Side" />
                </GridItem>
                <GridItem {...styles.mainColumn}>
                    {/* Store Custom Code - Start */}
                    {enableTopOrBottomZone && !showTopZoneAtBottom && (
                        <StyledWrapper {...styles.topOrBottomContentWrapper}>
                            <Zone contentId={id} zoneName={topOrBottomContentZoneName} />
                        </StyledWrapper>
                    )}
                    {/* Store Custom Code - End */}
                    <Zone contentId={id} zoneName="Content" />
                </GridItem>
                <GridItem {...styles.sideColumnDesktop}>
                    <StyledWrapper {...sideColumnWrapperStyles}>
                        <Zone contentId={id} zoneName="Side" />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            {/* Store Custom Code - Start */}
            {enableTopOrBottomZone && showTopZoneAtBottom && (
                <StyledWrapper {...styles.topOrBottomContentWrapper}>
                    <Zone contentId={id} zoneName={topOrBottomContentZoneName} />
                </StyledWrapper>
            )}
            {/* Store Custom Code - End */}
        </DataViewLoadingOverlay>
    );
};

export default OrderLayout;
